import React, { useEffect, useState } from 'react';
import { Container, Group, Box, ActionIcon, Stack, Flex, Image } from '@mantine/core';
import { ThemedText } from './Typography';
import { Link, usePage } from '@inertiajs/react';
import { useMediaQueryContext } from '../src/context/MediaQueryContext';
import { IconBrandFacebook, IconBrandInstagram, IconBrandYoutube, IconMail } from '@tabler/icons-react';
import { useUser } from '../src/context/UserContext';
import { useFoundingSpotsOpen, useSiteSwitch } from '../src/hooks/useSiteSwitch';
import PlatformDonationModal from './Modals/PlatformDonationModal';
import newLogoWhite from '@/assets/images/new-wcwd-logo-white.svg';
import { themeAlpha, themeVar, whiteTextGradient } from '../src/utils/pageTheme';
import { COLORS } from '../src/constants/theme';
import artistStarIcon from '@/assets/images/money-star-icon.png';
import ThemedButton from './ui/Buttons/ThemedButton';

const SOCIAL_LINKS = [
  { name: 'Facebook', href: 'https://www.facebook.com/wherecanwedance', Icon: IconBrandFacebook },
  { name: 'Instagram', href: 'https://www.instagram.com/where.can.we.dance/', Icon: IconBrandInstagram },
  { name: 'YouTube', href: 'https://www.youtube.com/@WhereCanWeDance', Icon: IconBrandYoutube },
];

const Footer = () => {
  const { user } = useUser();
  const calculatorPublic = useSiteSwitch('break_even_calculator_public');
  const foundingSpotsOpen = useFoundingSpotsOpen();
  const [donationModalOpened, setDonationModalOpened] = useState(false);

  // Get Stripe key from body data attribute
  const stripePublishableKey = document.body.getAttribute('data-stripe-public-key') || '';
  useEffect(() => {
    const fixFooterPosition = () => {
      const appContainer = document.querySelector('.app-container') || document.querySelector('#app');
      const footer = document.querySelector('footer');
      const mainContent = document.querySelector('main') || document.querySelector('.main-content');

      if (!footer || !appContainer) return;

      if (mainContent && footer.compareDocumentPosition(mainContent) & Node.DOCUMENT_POSITION_FOLLOWING) {
        appContainer.appendChild(footer);
      }

      footer.style.marginTop = 'auto';
      footer.style.width = '100%';
    };

    setTimeout(fixFooterPosition, 100);
    window.addEventListener('popstate', fixFooterPosition);

    return () => {
      window.removeEventListener('popstate', fixFooterPosition);
    };
  }, []);

  const { isMobile, isTablet, isDesktop } = useMediaQueryContext();

  return (
    <Box
      component="footer"
      bg="#0f0f0f"
      py="md"
      style={{ color: 'white', width: '100%', height: 'auto', marginTop: 'auto' }}
    >
      <Container size="lg">
        {(isDesktop || isTablet || isMobile) && (
          <Flex justify="space-between" gap="md" mb="8rem" direction={isTablet || isMobile ? 'column' : 'row'}>
            <Stack gap="lg" align={isMobile ? 'flex-start' : 'flex-start'}>
              <Box>
                <img
                  src={newLogoWhite}
                  alt="Where Can We Dance Logo"
                  loading="lazy"
                  className="site-footer-logo"
                  style={{ marginTop: '12px', height: isMobile ? '30px' : '40px', width: 'auto' }}
                />
              </Box>
              <Box>
                <ThemedText mb="md" size="md" fw={700} color="#f2f2f290" ta={isMobile ? 'center' : 'left'}>
                  Follow Us
                </ThemedText>
                <Group gap="xs" justify={isMobile ? 'center' : 'flex-start'}>
                  {/* One element per icon: a button inside a link is two tab
                      stops and has no name for screen readers. #1895 */}
                  {SOCIAL_LINKS.map(({ name, href, Icon }) => (
                    <ActionIcon
                      key={name}
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`WhereCanWeDance on ${name} (opens in a new tab)`}
                      size="lg"
                      variant="subtle"
                      color="gray"
                    >
                      <Icon size={22} aria-hidden="true" />
                    </ActionIcon>
                  ))}
                </Group>
              </Box>
            </Stack>

            <Flex gap="xl" direction={isMobile ? 'column' : 'row'} align={isMobile ? 'flex-start' : 'flex-start'}>
              <Box>
                <ThemedText my={16} fw={700} color="#f2f2f290" ta={isMobile ? 'center' : 'left'}>
                  Quick Links
                </ThemedText>
                <Stack gap="lg" align={isMobile ? 'flex-start' : 'flex-start'}>
                  <ThemedText component={Link} href="/about" size="sm" color="gray.3">
                    About
                  </ThemedText>
                  <ThemedText component={Link} href="/artist-stars" size="sm" color="gray.3">
                    Artist Stars
                  </ThemedText>
                  <ThemedText component={Link} href="/artist-pro" size="sm" color="gray.3">
                    Artist Pro
                  </ThemedText>
                  <ThemedText component={Link} href="/contact" size="sm" color="gray.3">
                    Contact us
                  </ThemedText>
                  <ThemedText component={Link} href="/updates" size="sm" color="gray.3">
                    What's New
                  </ThemedText>
                  <ThemedText component={Link} href="/help" size="sm" color="gray.3">
                    Help Center
                  </ThemedText>
                </Stack>
              </Box>

              <Box>
                <ThemedText my={16} fw={700} color="#f2f2f290" ta={isMobile ? 'left' : 'left'}>
                  Resources
                </ThemedText>
                <Stack gap="md" align={isMobile ? 'flex-start' : 'flex-start'}>
                  {calculatorPublic && (
                    <ThemedText component={Link} href="/tools/break-even-calculator" size="sm" color="gray.3">
                      Break-even Calculator
                    </ThemedText>
                  )}
                  {foundingSpotsOpen && (
                    <ThemedText component={Link} href="/founding-organizer" size="sm" color="gray.3">
                      Founding Organizer
                    </ThemedText>
                  )}
                  <ThemedText
                    component={Link}
                    href="/suggest_artist"
                    size="sm"
                    color="gray.3"
                    style={{ textDecoration: 'none' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = themeVar('accent-text'))}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                  >
                    Add A New Artist
                  </ThemedText>
                  <ThemedText
                    component={Link}
                    href="/suggest_event"
                    size="sm"
                    color="gray.3"
                    style={{ textDecoration: 'none' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = themeVar('accent-text'))}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                  >
                    Add A New Event
                  </ThemedText>
                  {/* Community Guidelines and GDPR were links to "#". A missing
                      link is honest; a dead one looks broken — and with Paris
                      attendees buying tickets, a GDPR link that goes nowhere is
                      worse than none. They return when there is a page. #1127 */}
                  <ThemedText component={Link} href="/terms-of-service" size="sm" color="gray.3">
                    Terms of Service
                  </ThemedText>
                  <ThemedText component={Link} href="/privacy" size="sm" color="gray.3">
                    Privacy Policy
                  </ThemedText>
                </Stack>
              </Box>
            </Flex>

            <Stack gap="xl" style={{ maxWidth: '300px' }} align={isMobile ? 'flex-start' : 'flex-start'}>
              <Box>
                <ThemedText my={16} fw={700} color="#f2f2f290" ta={isMobile ? 'left' : 'left'}>
                  Newsletter
                </ThemedText>
                <ThemedText size="sm" color="gray.3" ta={isMobile ? 'left' : 'left'}>
                  Be the first to know about our future updates!
                </ThemedText>
                <ThemedButton
                  component={Link}
                  href="/subscribe"
                  size="lg"
                  fullWidth
                  mt={16}
                  className="poppins"
                  leftSection={<IconMail size={18} />}
                  styles={{
                    root: {
                      // White text on a fill deep enough for it, on every theme (#2344).
                      background: whiteTextGradient(),
                      color: COLORS.textOnAccent,
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: `0 0 18px ${themeAlpha(0.45)}`,
                      textTransform: 'none',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                    },
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = `0 0 24px ${themeAlpha(0.7)}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 0 18px ${themeAlpha(0.45)}`;
                  }}
                >
                  Subscribe
                </ThemedButton>
              </Box>
              <Box>
                <ThemedText my={16} fw={700} color="#f2f2f290" ta={isMobile ? 'left' : 'left'}>
                  Support this project
                </ThemedText>
                <ThemedText size="sm" color="gray.3" ta={isMobile ? 'left' : 'left'}>
                  We're consistently working on new releases.
                </ThemedText>
                <ThemedButton
                  size="lg"
                  fullWidth
                  mt={16}
                  className="poppins"
                  leftSection={
                    <img
                      src={artistStarIcon}
                      alt="Star"
                      loading="lazy"
                      style={{ width: '40px', height: '36px', marginRight: '0px' }}
                    />
                  }
                  styles={{
                    root: {
                      // White text on a fill deep enough for it, on every theme (#2344).
                      background: whiteTextGradient(),
                      color: COLORS.textOnAccent,
                      border: 'none',
                      borderRadius: '8px',
                      boxShadow: `0 0 18px ${themeAlpha(0.45)}`,
                      textTransform: 'none',
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
                    },
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = `0 0 24px ${themeAlpha(0.7)}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 0 18px ${themeAlpha(0.45)}`;
                  }}
                  onClick={() => setDonationModalOpened(true)}
                >
                  Donate a star!
                </ThemedButton>
              </Box>
            </Stack>
          </Flex>
        )}
      </Container>

      {/* Platform Donation Modal */}
      <PlatformDonationModal
        opened={donationModalOpened}
        onClose={() => setDonationModalOpened(false)}
        user={user}
        stripePublishableKey={stripePublishableKey}
      />
    </Box>
  );
};

export default Footer;
